import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { FilterChip } from "@/components/design/FilterChip";
import { type KeywordMatch } from "@/lib/tobis";
import { useKeywordSuggestions } from "./use-keyword-suggestions";

// Tonalitaets-/Stoff-Suche ueber TMDB-Keywords, in der Titel-Filterleiste.
//
// Keine Variante von MultiSelect (components/design/): dort kommt die volle
// Optionsliste vorab aus filterOptions() und wird client-seitig gefiltert -
// bei rund 19.700 Keywords im Bestand waere das weder ladbar noch
// durchsuchbar. Hier schickt jede Eingabe (entprellt) eine Serveranfrage
// (searchKeywords), die Auswahl traegt deshalb Paare {keywordId, keyword}
// statt nur IDs, weil der Klartext fuer die Chips gebraucht wird und nicht
// aus einer lokalen Liste nachschlagbar ist.
//
// Semantisch getrennt von TobisTitleFilters: keywordIds ist im BFF ein
// Rang-Signal ueber der vollen Kandidatenmenge, kein Ausschlusskriterium, und
// schliesst sich mit Cursor-Paging aus (siehe routes/tobis.ts). Der
// aufrufende Code (TobisTitlesPage) haelt die Auswahl deshalb als eigenen
// State, nicht als Teil von TobisTitleFilters.

export function TobisKeywordFilter({
  selected,
  onChange,
  matchAll = false,
  onMatchAllChange,
}: {
  selected: readonly KeywordMatch[];
  onChange: (next: KeywordMatch[]) => void;
  /** "Oder" (Default, mindestens ein Keyword zaehlt) vs. "Und" (alle gewaehlten). Nur eine Rang-, keine Ausschlussregel - siehe TitleFilters.keywordMatchAll. */
  matchAll?: boolean;
  onMatchAllChange?: (next: boolean) => void;
}) {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const { suggestions, loading } = useKeywordSuggestions(query);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (
        ref.current &&
        e.target instanceof Node &&
        !ref.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);


  const visibleMatches = suggestions;

  const selectedIds = new Set(selected.map((k) => k.keywordId));
  const add = (match: KeywordMatch) => {
    if (selectedIds.has(match.keywordId)) return;
    onChange([...selected, match]);
    setQuery("");
  };
  const remove = (keywordId: string) =>
    onChange(selected.filter((k) => k.keywordId !== keywordId));

  return (
    <div className="flex items-center gap-2">
      <div ref={ref} className="relative w-56">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex min-h-[34px] w-full flex-wrap items-center gap-1 rounded bg-surface px-2 py-1 text-left ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-accent"
        >
          {selected.length === 0 ? (
            <span className="text-[13px] text-ink/45">
              {intl.formatMessage({
                id: "tobis.filter.tone",
                defaultMessage: "Tone / subject",
              })}
            </span>
          ) : (
            selected.map((k) => (
              <span
                key={k.keywordId}
                className="inline-flex items-center gap-1 rounded-full bg-ink/[0.06] px-2 py-0.5 text-[11px] text-ink/80"
              >
                {k.keyword}
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(k.keywordId);
                  }}
                  className="cursor-pointer text-ink/40 hover:text-ink"
                >
                  × {/* i18n-allow */}
                </span>
              </span>
            ))
          )}
        </button>

        {open ? (
          <div className="absolute left-0 right-0 z-50 mt-1 max-h-64 overflow-auto rounded-md bg-surface shadow-lg ring-1 ring-ink/10">
            <div className="sticky top-0 bg-surface p-2">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "tobis.filter.toneSearch",
                  defaultMessage: "e.g. heist, coming-of-age …",
                })}
                className="w-full rounded bg-surface px-2 py-1 text-[13px] text-ink ring-1 ring-ink/10 placeholder:text-ink/40 focus:outline-none focus:ring-ink/30"
              />
            </div>
            {loading ? (
              <div className="px-3 py-2 text-[12px] text-ink/45">
                {intl.formatMessage({
                  id: "tobis.filter.toneLoading",
                  defaultMessage: "Searching…",
                })}
              </div>
            ) : query.trim() && visibleMatches.length === 0 ? (
              <div className="px-3 py-2 text-[12px] text-ink/45">
                {intl.formatMessage({
                  id: "search.noResults",
                  defaultMessage: "No matches",
                })}
              </div>
            ) : (
              visibleMatches.map((m) => (
                <label
                  key={m.keywordId}
                  className="flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-[13px] text-ink/80 hover:bg-ink/[0.06]"
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(m.keywordId)}
                      onChange={() =>
                        selectedIds.has(m.keywordId)
                          ? remove(m.keywordId)
                          : add(m)
                      }
                      className="accent-accent"
                    />
                    <span className="truncate">{m.keyword}</span>
                  </span>
                  <span className="shrink-0 text-[11px] text-ink/40 tabular-nums">
                    {m.titleCount}
                  </span>
                </label>
              ))
            )}
          </div>
        ) : null}
      </div>
      {/* Nur ab zwei Keywords sichtbar: bei 0 oder 1 gibt es keinen
          Unterschied zwischen "mindestens eines" und "alle". */}
      {selected.length >= 2 && onMatchAllChange ? (
        <div className="flex shrink-0 gap-1">
          <FilterChip
            active={!matchAll}
            onClick={() => onMatchAllChange(false)}
          >
            {intl.formatMessage({
              id: "tobis.filter.toneAny",
              defaultMessage: "Any",
            })}
          </FilterChip>
          <FilterChip active={matchAll} onClick={() => onMatchAllChange(true)}>
            {intl.formatMessage({
              id: "tobis.filter.toneAll",
              defaultMessage: "All",
            })}
          </FilterChip>
        </div>
      ) : null}
    </div>
  );
}
